import apiClient from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TargetType = 'user' | 'team' | 'all';
export type GranteeType = 'user' | 'team';
export type PermissionType = 'timesheet_approve' | 'timesheet_view';

export interface PermissionRule {
  id: string;
  targetType: TargetType;
  targetId: string | null;
  permission: PermissionType;
  granteeType: GranteeType;
  granteeId: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePermissionRulePayload {
  targetType: TargetType;
  targetId: string | null;
  permission: PermissionType;
  granteeType: GranteeType;
  granteeId: string;
}

/** A single approver entry (user or team). */
export interface ApproverEntry {
  granteeId: string;
  granteeType: 'user' | 'team';
}

/** Response from the my-approver endpoint. */
export interface MyApproverResponse {
  approvers: ApproverEntry[];
}

/** Response from the my-viewable-targets endpoint. */
export interface MyViewableTargetsResponse {
  canViewAll: boolean;
  targets: Array<{
    targetType: TargetType;
    targetId: string | null;
    permission: PermissionType;
  }>;
}

// ─── API Functions ────────────────────────────────────────────────────────────

/** Get the targets (users/teams) the current user can view in timesheets. */
export async function getMyViewableTargets(): Promise<MyViewableTargetsResponse> {
  const { data } = await apiClient.get<MyViewableTargetsResponse>(
    '/permissions/my-viewable-targets',
  );
  return data;
}

/** Get the current user's timesheet approver (resolved from permission rules). */
export async function getMyTimesheetApprover(): Promise<MyApproverResponse> {
  const { data } = await apiClient.get<MyApproverResponse>(
    '/permissions/my-approver',
  );
  return data;
}

export async function getPermissionRules(
  permission: PermissionType,
): Promise<PermissionRule[]> {
  const { data } = await apiClient.get<PermissionRule[]>(
    '/permissions/rules',
    { params: { permission } },
  );
  return data;
}

export async function createPermissionRule(
  payload: CreatePermissionRulePayload,
): Promise<PermissionRule> {
  const { data } = await apiClient.post<PermissionRule>(
    '/permissions/rules',
    payload,
  );
  return data;
}

export async function createPermissionRulesBulk(
  rules: CreatePermissionRulePayload[],
): Promise<PermissionRule[]> {
  const { data } = await apiClient.post<PermissionRule[]>(
    '/permissions/rules/bulk',
    { rules },
  );
  return data;
}

export async function deletePermissionRule(id: string): Promise<void> {
  await apiClient.delete(`/permissions/rules/${id}`);
}

export async function deletePermissionRulesBulk(ids: string[]): Promise<void> {
  await apiClient.delete('/permissions/rules/bulk', { data: { ids } });
}
